import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Company} from '../_models/company';
import {EffectiveRole, User} from '../_models/user';
import {TargetCompanyService} from './target-company.service';
import {environment} from '../../environments/environment';

export interface WorkspaceDestination {
  external: boolean;
  url: string;
}

export const PENDING_ACTION_QUERY_PARAM = 'pendingAction';
export const PENDING_ACTION_EMAIL_NOTIFICATION_SETTINGS = 'emailNotificationSettings';

@Injectable({
  providedIn: 'root'
})
export class CompanyWorkspaceService {
  private readonly legacyPreferredCompanyKey = 'colorjob_preferred_company_id';
  private readonly autoOpenCompanyKey = 'colorjob_auto_open_company';
  private readonly mainDomain = 'colorjob.com';

  constructor(
    private router: Router,
    private targetCompanyService: TargetCompanyService
  ) {
  }

  getPreferredCompanyId(): number | null {
    if (!this.isAutoOpenEnabled()) {
      return null;
    }

    return this.targetCompanyService.getTargetCompanyId();
  }

  isAutoOpenPreferredCompanyEnabled(): boolean {
    return this.isAutoOpenEnabled();
  }

  savePreferredCompany(companyId: number): void {
    localStorage.setItem(this.autoOpenCompanyKey, 'true');
    this.targetCompanyService.setTargetCompanyId(companyId);
    this.clearLegacyPreferredCompany();
  }

  rememberLastCompany(companyId: number): void {
    this.targetCompanyService.setTargetCompanyId(companyId);
    this.clearLegacyPreferredCompany();
  }

  clearPreferredCompany(): void {
    localStorage.removeItem(this.autoOpenCompanyKey);
    this.clearLegacyPreferredCompany();
  }

  clearCurrentCompanyContext(companyId?: number | null): void {
    const storedTargetCompanyId = this.targetCompanyService.getTargetCompanyId();

    if (!companyId || storedTargetCompanyId === companyId) {
      this.targetCompanyService.clearTargetCompanyId();
    }
  }

  clearStoredCompanyContext(companyId?: number | null): void {
    const preferredCompanyId = this.getPreferredCompanyId();

    this.clearCurrentCompanyContext(companyId);

    if (!companyId || preferredCompanyId === companyId) {
      this.clearPreferredCompany();
    }
  }

  resolvePreferredCompany(user: User | null | undefined): Company | null {
    const preferredCompanyId = this.getPreferredCompanyId();
    if (!preferredCompanyId || !user?.companies?.length) {
      return null;
    }

    const preferredCompany = user.companies.find((company) => company.id === preferredCompanyId) ?? null;
    if (!preferredCompany) {
      this.clearPreferredCompany();
      return null;
    }

    return preferredCompany;
  }

  getHostMatchedCompany(user: User | null | undefined): Company | null {
    const currentSubdomain = this.getCurrentSubdomain();
    if (!currentSubdomain || currentSubdomain === 'app') {
      return null;
    }

    return user?.companies?.find((company) => company.subdomain?.toLowerCase() === currentSubdomain) ?? null;
  }

  shouldShowWelcomeScreen(user: User | null | undefined): boolean {
    if (!user || user.effectiveRole === EffectiveRole.ROLE_SUPER_ADMIN) {
      return false;
    }

    if ((user.companies?.length ?? 0) <= 1) {
      return false;
    }

    return !this.resolvePreferredCompany(user);
  }

  resolvePostLoginDestination(user: User | null | undefined): WorkspaceDestination {
    if (!user) {
      return {external: false, url: '/login'};
    }

    if (user.effectiveRole === EffectiveRole.ROLE_SUPER_ADMIN) {
      return {external: false, url: '/dashboard'};
    }

    const companies = user.companies ?? [];
    const hostMatchedCompany = this.getHostMatchedCompany(user);
    if (hostMatchedCompany) {
      this.targetCompanyService.setTargetCompanyId(hostMatchedCompany.id);
      return this.getCompanyDestination(hostMatchedCompany, true, user);
    }

    const preferredCompany = this.resolvePreferredCompany(user);
    if (preferredCompany) {
      this.targetCompanyService.setTargetCompanyId(preferredCompany.id);
      return this.getCompanyDestination(preferredCompany, false, user);
    }

    if (this.isAppWorkspaceHost() && companies.length > 1) {
      this.clearCurrentCompanyContext();
      return this.getAllCompaniesDestination();
    }

    if (companies.length === 1) {
      this.rememberLastCompany(companies[0].id);
      return this.getCompanyDestination(companies[0], false, user);
    }

    if (!companies.length) {
      this.targetCompanyService.clearTargetCompanyId();
      return {external: false, url: '/welcome'};
    }

    this.targetCompanyService.clearTargetCompanyId();
    return {external: false, url: '/welcome'};
  }

  getCompanyDestination(
    company: Company,
    forceInternalNavigation = false,
    user?: User | null
  ): WorkspaceDestination {
    if (!company?.id) {
      return {external: false, url: '/login'};
    }

    const internalCompanyUrl = this.getCompanyHomeUrl(company.id, user?.effectiveRole);

    const currentSubdomain = this.getCurrentSubdomain();
    if (forceInternalNavigation || (currentSubdomain && currentSubdomain === company.subdomain?.toLowerCase())) {
      return {
        external: false,
        url: internalCompanyUrl
      };
    }

    const externalUrl = this.getExternalCompanyUrl(company, internalCompanyUrl);
    if (externalUrl) {
      return {
        external: true,
        url: externalUrl
      };
    }

    return {
      external: false,
      url: internalCompanyUrl
    };
  }

  getCompanyHomeUrl(companyId: number, effectiveRole?: EffectiveRole | null): string {
    return `/company/${companyId}/dashboard`;
  }

  appendPendingAction(destination: WorkspaceDestination, action: string | null | undefined): WorkspaceDestination {
    if (!action) {
      return destination;
    }

    return {
      ...destination,
      url: this.appendQueryParam(destination.url, PENDING_ACTION_QUERY_PARAM, action)
    };
  }

  private appendQueryParam(url: string, key: string, value: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  }

  navigateToDestination(destination: WorkspaceDestination): void {
    if (destination.external) {
      window.location.assign(destination.url);
      return;
    }

    this.router.navigateByUrl(destination.url);
  }

  isAppWorkspaceHost(): boolean {
    const hostname = window.location.hostname.toLowerCase();

    if (!hostname.endsWith(this.mainDomain)) {
      return true;
    }

    const subdomain = this.getCurrentSubdomain();
    return !subdomain || subdomain === 'app';
  }

  private isAutoOpenEnabled(): boolean {
    return localStorage.getItem(this.autoOpenCompanyKey) === 'true';
  }

  private clearLegacyPreferredCompany(): void {
    localStorage.removeItem(this.legacyPreferredCompanyKey);
  }

  getAllCompaniesDestination(skipAutoOpen = false): WorkspaceDestination {
    const welcomePath = skipAutoOpen ? '/welcome?showCompanies=true' : '/welcome';
    const externalUrl = this.getAppWorkspaceUrl(welcomePath);

    if (externalUrl) {
      return {
        external: true,
        url: externalUrl
      };
    }

    return {
      external: false,
      url: welcomePath
    };
  }

  getCompanySwitcherDestination(company: Company): WorkspaceDestination {
    if (!company?.id) {
      return {external: false, url: '/login'};
    }

    const currentSubdomain = this.getCurrentSubdomain();
    if (currentSubdomain && currentSubdomain === company.subdomain?.toLowerCase()) {
      return {
        external: false,
        url: `/company/${company.id}/dashboard`
      };
    }

    const externalUrl = this.getExternalCompanyUrl(company, `/company/${company.id}/dashboard`);
    if (externalUrl) {
      return {
        external: true,
        url: externalUrl
      };
    }

    return {
      external: false,
      url: `/company/${company.id}/dashboard`
    };
  }

  private getExternalCompanyUrl(company: Company, pathname = ''): string | null {
    if (!company?.subdomain || !this.canUseExternalCompanyHost()) {
      return null;
    }

    const normalizedPath = pathname.startsWith('/') || !pathname
      ? pathname
      : `/${pathname}`;

    return `${window.location.protocol}//${company.subdomain}.colorjob.com${normalizedPath}`;
  }

  private getAppWorkspaceUrl(pathname = ''): string | null {
    if (!this.canUseExternalCompanyHost()) {
      return null;
    }

    const normalizedPath = pathname.startsWith('/') || !pathname
      ? pathname
      : `/${pathname}`;

    return `${window.location.protocol}//app.${this.mainDomain}${normalizedPath}`;
  }

  private canUseExternalCompanyHost(): boolean {
    return environment.production;
  }

  private getCurrentSubdomain(): string | null {
    const hostname = window.location.hostname.toLowerCase();

    if (hostname === this.mainDomain || !hostname.endsWith(`.${this.mainDomain}`)) {
      return null;
    }

    const subdomain = hostname.substring(0, hostname.length - this.mainDomain.length - 1);
    return subdomain && !subdomain.includes('.') ? subdomain : null;
  }
}
